import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { typesOfSaleStatistic } from '../../filters/MePosSaleFilters/constants';
import { getPeriodStringDotFormat } from '../../../../hooks/helpers';
import ExportSalesBtn from '../ExportSalesBtn';
import { safeStringify } from './helpers';
import { shortSaleStatisticsColumns } from '../utils/constants';
import { maybeTranslate } from '../utils/helpers';

const ShortSaleStatistics = ({
                               summary = null,
                               mainColumns = [],
                               tableRows = [],
                               tableRowsRaw = [],
                             }) => {
  const { t } = useTranslation();
  const currency = summary?.currency || '';
  const {
    periodStart,
    periodEnd,
    statisticType,
    employee,
    category,
    brand,
    productSkuBarcode,
    warehouse,
    categoryLabel,
  } = useSelector(state => state.saleFiltersSlice);

  const getDisplayValue = (field, raw) => {
    if (!raw && raw !== 0) return null;

    switch (field) {
      case 'category':
        if (!raw?.categoryId) return null;
        const catName = raw.categoryName ?? '';
        const subName = raw.subCategoryName ?? '';
        if (subName && catName) return `${subName} (${catName})`;
        return catName || null;

      case 'employee':
      case 'warehouse':
      case 'brand':
        if (typeof raw === 'object' && raw !== null) {
          return safeStringify(raw.label ?? raw.key ?? raw.name ?? raw);
        }
        return safeStringify(raw);

      default:
        return safeStringify(raw);
    }
  };

  // form an array of visible selected filters
  const selectedFilters = useMemo(() => {
    const list = [];

    if (periodStart || periodEnd) {
      const dateValue = getPeriodStringDotFormat(periodStart, periodEnd);
      list.push({ label: 'salesStatistics.Operation date range', value: dateValue });
    }

    if (statisticType) {
      const statObj = typesOfSaleStatistic.find(s => s.key === statisticType);
      const statLabel = statObj ? (typeof statObj.label === 'string' ? t(statObj.label) : safeStringify(statObj.label)) : safeStringify(statisticType);
      list.push({
        label: 'salesStatistics.Statistics', value: statLabel
      });
    }

    const empVal = getDisplayValue('employee', employee);
    if (empVal) list.push({ label: 'salesFilters.Employee', value: empVal });

    const catVal = getDisplayValue('category', categoryLabel);
    if (catVal) list.push({ label: 'salesFilters.Category', value: catVal });

    const brandVal = getDisplayValue('brand', brand);
    if (brandVal) list.push({ label: 'salesFilters.Brand', value: brandVal });

    if (productSkuBarcode) {
      list.push({ label: 'salesFilters.Product / SKU / Barcode', value: safeStringify(productSkuBarcode) });
    }

    const whVal = getDisplayValue('warehouse', warehouse);
    if (whVal) list.push({ label: 'salesFilters.Warehouse', value: whVal });

    return list;
  }, [periodStart, periodEnd, statisticType, employee, category, brand, productSkuBarcode, warehouse, categoryLabel, t]);

  return (
    <div className="overflow-auto">
      <div style={{ minWidth: '880px' }}>
        <div className="d-flex flex-nowrap bg-violet-light p-3 px-4">
          {selectedFilters.map((filter, idx) => (
            <div key={idx} className="d-flex flex-column me-4 text-dark">
              <span>{t(filter.label)}</span>
              <span className="fw-bold fs-6">{maybeTranslate(filter.value)}</span>
            </div>
          ))}
          <div className="ms-auto">
            <ExportSalesBtn
              mainColumns={mainColumns}
              tableRowsRaw={tableRowsRaw}
              tableRowsArray={tableRows}
              currency={currency}
              filenameBase={statisticType ? statisticType : 'sales_report'}
            />
          </div>
        </div>
        <div className="bg-violet p-3 px-4">
          <Row>
            <Col className="d-flex align-items-center">
              <span className="fw-bold fs-6 text-dark">{t('salesStatistics.Period Summary')}</span>
            </Col>
            {shortSaleStatisticsColumns.map(({ key, label, formatter }) => {
              const raw = summary ? summary[key] : null;
              const value = formatter && formatter(raw, currency);
              return (
                <Col key={key} className="d-flex flex-column text-dark">
                  <span>{t(label)}</span>
                  <span className="fw-bold fs-6">{value}</span>
                </Col>
              );
            })}
          </Row>
        </div>
      </div>
    </div>
  );
};

export default ShortSaleStatistics;